import type { MegamapProject } from "./data";

export type ColorDim = "needs" | "wpw" | "deadline" | "status" | "coordinator";

/** What a single-project marker's radius scales with. */
export type SizeDim = "uniform" | "value" | "unagreed" | "length" | "parcels";

export const SIZE_DIMS: { value: SizeDim; label: string }[] = [
  { value: "uniform", label: "Stały" },
  { value: "value", label: "Wartość projektu" },
  { value: "unagreed", label: "Działki pryw. do przekazania" },
  { value: "length", label: "Długość trasy" },
  { value: "parcels", label: "Liczba działek" },
];

export function sizeMetric(dim: SizeDim, p: MegamapProject): number {
  switch (dim) {
    case "uniform":
      return 1;
    case "value":
      return p.total_value || 0;
    case "unagreed":
      return p.private_unagreed_parcels;
    case "length":
      return p.route_length_m || 0;
    case "parcels":
      return p.parcel_count;
  }
}

export const COLOR_DIMS: { value: ColorDim; label: string }[] = [
  { value: "needs", label: "Do przydziału podwykonawcy" },
  { value: "wpw", label: "Ma zamówienie WPW" },
  { value: "deadline", label: "Termin" },
  { value: "status", label: "Status projektu" },
  { value: "coordinator", label: "Koordynator" },
];

const GRAY = "#94a3b8";

// Stable palette for the categorical (coordinator) colouring.
const PALETTE = [
  "#dc2626", "#2563eb", "#16a34a", "#d97706", "#7c3aed",
  "#0891b2", "#db2777", "#65a30d", "#ea580c", "#0d9488",
  "#4f46e5", "#b91c1c",
];

function hashColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

const DEADLINE_COLORS: Record<string, string> = {
  overdue: "#dc2626",
  soon: "#ea580c",
  approaching: "#d97706",
  ok: "#16a34a",
  none: GRAY,
};

const STATUS_COLORS: Record<string, string> = {
  active: "#16a34a",
  suspended: "#d97706",
  inactive: GRAY,
};

export function colorFor(dim: ColorDim, p: MegamapProject): string {
  switch (dim) {
    case "needs":
      return p.needs_subcontractor ? "#dc2626" : GRAY;
    case "wpw":
      return p.has_wpw_order ? "#16a34a" : "#d97706";
    case "deadline":
      return DEADLINE_COLORS[p.deadline_status] ?? GRAY;
    case "status":
      return STATUS_COLORS[p.status] ?? GRAY;
    case "coordinator":
      return p.coordinator_ids.length ? hashColor(p.coordinator_ids[0]) : GRAY;
  }
}

export interface LegendEntry {
  color: string;
  label: string;
  count: number;
}

/** Build a legend for the active dimension from the rows currently shown. */
export function legendFor(dim: ColorDim, rows: MegamapProject[]): LegendEntry[] {
  const buckets = new Map<string, { color: string; count: number }>();
  const bump = (label: string, color: string) => {
    const b = buckets.get(label);
    if (b) b.count += 1;
    else buckets.set(label, { color, count: 1 });
  };
  for (const p of rows) {
    const color = colorFor(dim, p);
    switch (dim) {
      case "needs":
        bump(p.needs_subcontractor ? "Do przydziału" : "Pozostałe", color);
        break;
      case "wpw":
        bump(p.has_wpw_order ? "Ma WPW" : "Bez WPW", color);
        break;
      case "deadline":
        bump(deadlineLabel(p.deadline_status), color);
        break;
      case "status":
        bump(statusLabel(p.status), color);
        break;
      case "coordinator":
        bump(p.coordinator_names[0] ?? "Nieprzypisany", color);
        break;
    }
  }
  return [...buckets.entries()]
    .map(([label, b]) => ({ label, color: b.color, count: b.count }))
    .sort((a, b) => b.count - a.count);
}

function deadlineLabel(s: string): string {
  return (
    { overdue: "Przeterminowane", soon: "≤14 dni", approaching: "≤30 dni", ok: "Na czas", none: "Bez terminu" }[
      s
    ] ?? s
  );
}

function statusLabel(s: string): string {
  return { active: "Aktywny", suspended: "Wstrzymany", inactive: "Zamknięty" }[s] ?? s;
}
